/**
 * /sign-in, Olytic sign-in.
 *
 * Renders inside AuthShell (centered card on muted background, brand wordmark
 * above). Email + password with show/hide toggle. Loading state replaces
 * button content with spinner + "Signing in...". Errors render inline above
 * the submit button.
 *
 * Per UX-DESIGN.md § Auth pages, modern SaaS chrome, frictionless.
 */

import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell.tsx';
import { PasswordInput } from '../components/PasswordInput.tsx';
import { useRouteProgress } from '../components/RouteProgress.tsx';
import { pickAuthProgressCopy, useAuthElapsedProgress } from '../hooks/useAuthElapsedProgress.ts';
import { useSession } from '../hooks/useSession.tsx';
import { ApiError, getSignInResetCount, resetAndRedirectToSignIn, signIn } from '../lib/api.ts';

/**
 * Sub-agent HHHHH (2026-07-29) — max auto-recover attempts per session.
 * Beyond this cap, SignIn.tsx falls back to the manual retry callout
 * (the Push 100 Retry / Start Fresh buttons) so a persistent server
 * outage can't loop the reset flow forever.
 */
const AUTO_RESET_MAX_ATTEMPTS = 2;

/**
 * Sub-agent HHHHH — anything older than this = stale sign-in-in-progress
 * marker. If we see one on mount we wipe it (leftover from an aborted
 * attempt in a prior tab/session).
 */
const STALE_SIGNIN_MARKER_MS = 60_000;

/**
 * Validate a `next=` query param against a strict allowlist. Returns the
 * caller-supplied path if safe, otherwise falls back to `/home`.
 *
 * Why an allowlist:
 *   Open redirects (e.g. ?next=https://attacker.example.com) let phishing
 *   pages take over the post-sign-in destination. Only allow same-origin
 *   absolute paths under a known set of prefixes — anything else falls back
 *   to /home.
 *
 * Why /oauth is on the list:
 *   The MCP OAuth bridge sends signed-out users to /sign-in?next=/oauth/consent?req_id=...
 *   so the consent flow can resume after sign-in. Without honoring `next`,
 *   the user lands on /home and Claude Desktop never gets the auth code.
 */
function safeNextPath(raw: string | null): string {
  if (raw === null || raw === '') return '/home';
  // Must be an absolute path (starts with '/'), not an absolute URL.
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/home';
  // Allowlist the legitimate post-sign-in destinations.
  const allowed = ['/home', '/admin', '/oauth/'];
  if (allowed.some((prefix) => raw === prefix.replace(/\/$/, '') || raw.startsWith(prefix))) {
    return raw;
  }
  return '/home';
}

export function SignIn() {
  const { status, refresh, hydrate } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const progress = useRouteProgress();

  const nextParam = new URLSearchParams(location.search).get('next');
  const nextPath = safeNextPath(nextParam);
  // Push #88 (preempt G): pre-fill email from ?email=... when the user
  // arrives from post-reset / post-signout / post-account-deletion paths.
  // Saves a typed-it-twice friction step. URLSearchParams already decodes.
  const emailHint = new URLSearchParams(location.search).get('email') ?? '';
  // Push #87.1 — when ?account_closed=1, surface a one-time banner so the
  // user understands their workspace was just deleted (vs being stuck in a
  // weird "I just signed out, why am I here?" state). Banner clears on
  // first successful sign-in or sign-up.
  const accountClosed = new URLSearchParams(location.search).get('account_closed') === '1';
  // Push #91 — break the OAuth infinite-loop bug. When an upstream
  // /oauth/<vendor>/start endpoint rejects (e.g. unknown_vendor for a
  // connector that doesn't have a wired OAuth handler), it redirects to
  // /sign-in with ?oauth_error=<reason>&next=/oauth/<vendor>/start.
  // If the user is already authenticated, the auto-redirect below
  // (`window.location.replace(nextPath)`) would loop straight back to
  // the failing start URL → /sign-in → start → ... at ~1Hz. Surface
  // floods /api/me + /api/me/preferences + downstream fetches and the
  // app feels "stuck loading" until the tab is closed. The fix:
  // detect ?oauth_error=, suppress the auto-redirect, and render the
  // friendly error inline so the user knows what happened.
  const oauthError = new URLSearchParams(location.search).get('oauth_error') ?? '';
  // Sub-agent HHHHH (2026-07-29) — auto-recover flash params.
  //   ?expired=1 = arrived via a protected-page session probe failure
  //                (UserShell / AdminShell redirect). Show "Session
  //                expired" flash so the user knows what happened.
  //   ?reset=1   = arrived via resetAndRedirectToSignIn() after a
  //                sign-in fetch failure. Show "Sign-in refreshed"
  //                flash so the reload isn't jarring.
  //   ?stuck=1   = manual escape hatch (external link or hand-typed);
  //                treat same as reset trigger.
  //   ?_r=<ts>   = cache-bust only, no UX change.
  // Read once here; the mount effect below strips them from the URL so
  // they don't linger in browser history / bookmarks.
  const expiredFlash = new URLSearchParams(location.search).get('expired') === '1';
  const resetFlash =
    new URLSearchParams(location.search).get('reset') === '1' ||
    new URLSearchParams(location.search).get('stuck') === '1';

  const [email, setEmail] = useState(emailHint);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'wrong_password' | 'generic' | 'timeout' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Sub-agent NO-ABORT (2026-08-13) — elapsed-time driver for the
  // progressive copy transitions on the loading card. Ticks while
  // `submitting` is true; resets to 0 when the submit finishes.
  const elapsedMs = useAuthElapsedProgress(submitting);

  // Sub-agent NO-ABORT (2026-08-13) — Neon warmup ping. Fire-and-forget
  // on mount so cold Neon compute is already warm by the time the user
  // hits Submit. No response handling — even the network handoff kicks
  // compute up while the form is being filled in. Guarded against
  // firing when the user is already authenticated (auto-redirects out).
  useEffect(() => {
    if (status === 'authenticated') return;
    try {
      void fetch('/api/health/warm', {
        method: 'GET',
        credentials: 'omit',
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      /* noop — best-effort warmup */
    }
  }, [status]);
  // Sub-agent GGGGG (2026-07-29) — Track 3. Persistent stall banner
  // when the last submit hit the timeout budget in signIn(). Distinct
  // from `error` because the stall failure mode carries a different
  // recovery affordance (retry + hard sign-out reset link) than a
  // bad-password rejection. Sub-agent HHHHH: only surfaces when the
  // auto-recover budget is exhausted (>= AUTO_RESET_MAX_ATTEMPTS
  // resets per session).
  const [timedOut, setTimedOut] = useState(false);

  // Sub-agent HHHHH (2026-07-29) — mount-time auto-recover.
  //
  // Every time SignIn mounts we sweep for stale state:
  //   (a) sessionStorage 'olytic:signin:started_at' older than 60s —
  //       leftover from an aborted attempt (tab close mid-flow, hard
  //       reload during the request, etc.). Silent wipe.
  //   (b) Transient URL params (_r, stuck, expired, reset) after we've
  //       captured them into flash state. history.replaceState so the
  //       URL settles to plain /sign-in on the next paint — bookmarks
  //       and back-button state stay clean.
  //
  // Runs once per mount. The captured flash values are stored in
  // useState above (expiredFlash / resetFlash), so stripping the URL
  // here doesn't erase the flash message.
  useEffect(() => {
    // (a) Stale in-progress marker.
    try {
      const startedAt = window.sessionStorage.getItem('olytic:signin:started_at');
      if (startedAt !== null) {
        const t = Number(startedAt);
        if (!Number.isFinite(t) || Date.now() - t > STALE_SIGNIN_MARKER_MS) {
          window.sessionStorage.removeItem('olytic:signin:started_at');
        }
      }
    } catch {
      /* SSR / storage-disabled — best-effort */
    }
    // (b) Strip transient params from URL.
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      let changed = false;
      for (const key of ['_r', 'stuck', 'expired', 'reset']) {
        if (params.has(key)) {
          params.delete(key);
          changed = true;
        }
      }
      if (changed) {
        const nextSearch = params.toString();
        const nextUrl = `${url.pathname}${nextSearch !== '' ? `?${nextSearch}` : ''}${url.hash}`;
        window.history.replaceState({}, '', nextUrl);
      }
    } catch {
      /* noop — history API missing (unlikely in-browser) */
    }
    // Intentional empty deps — mount only. Re-firing on location change
    // would fight the strip we just did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When already authenticated, redirect to nextPath. For /oauth/* paths we
  // MUST do a full-page navigation (window.location), not React Router's
  // <Navigate>, because /oauth/upstream/start is a Pages Function — not a
  // SPA route. Client-side routing would render the SPA 404 page instead of
  // letting the Pages Function intercept the request.
  //
  // Push #91 — suppress the auto-redirect when ?oauth_error= is present.
  // Otherwise we loop forever between /sign-in and /oauth/<vendor>/start
  // when the start handler rejects (e.g. unknown_vendor for a connector
  // without a wired OAuth path). See the oauthError extraction above.
  const isOauthNext = nextPath.startsWith('/oauth/');
  const shouldAutoRedirect = isOauthNext && oauthError === '';
  useEffect(() => {
    if (status === 'authenticated' && shouldAutoRedirect) {
      window.location.replace(nextPath);
    }
  }, [status, shouldAutoRedirect, nextPath]);

  if (status === 'authenticated') {
    if (shouldAutoRedirect) {
      // Showing a stable loading frame while the useEffect above triggers
      // the full-page nav. Returning null here would flash a blank page.
      return (
        <AuthShell title="Redirecting…" subtitle="One moment.">
          <div className="auth-loading">
            <span className="spinner spinner-lg" />
            <p className="muted" style={{ margin: '14px 0 0', fontSize: '13px' }}>
              Continuing your sign-in flow.
            </p>
          </div>
        </AuthShell>
      );
    }
    // Authenticated + oauth_error present — render an AuthShell with the
    // error so the user understands what happened and can navigate back.
    // No auto-redirect (would loop). No <Navigate> to nextPath either,
    // since nextPath might be the same failing /oauth/<vendor>/start.
    if (oauthError !== '') {
      return (
        <AuthShell
          title="Couldn't complete connector setup"
          subtitle="You're signed in, but the connector couldn't start its sign-in flow."
        >
          <div className="callout callout-danger" style={{ marginBottom: 16 }}>
            <p className="callout-body" style={{ margin: 0 }}>
              {oauthError.startsWith('unknown_vendor:')
                ? `This connector isn't available yet (${oauthError.slice('unknown_vendor:'.length)}). Try a different one, or contact support if you keep hitting this.`
                : `We couldn't start that connector (${oauthError}). Give it another try, or contact support if it keeps happening.`}
            </p>
          </div>
          <Link
            to="/home"
            className="btn btn-primary btn-block"
            style={{ textDecoration: 'none', textAlign: 'center' }}
          >
            Back to home
          </Link>
        </AuthShell>
      );
    }
    return <Navigate to={nextPath} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setErrorKind(null);

    if (email.trim() === '' || password === '') {
      setError('Email and password are required.');
      setErrorKind('generic');
      return;
    }

    setSubmitting(true);
    progress.start();
    try {
      const res = await signIn(email.trim(), password);
      // Reached the happy path — clear any prior stall banner.
      setTimedOut(false);
      // Phase 1.35 (Ean dogfood 2026-06-30 — first-load hang fix). Mirror
      // Push #88's signup + accept-invite hydration-from-body pattern.
      // /api/signin now returns the full CurrentUser shape so the SPA can
      // flip status='authenticated' synchronously without firing /api/me
      // before the Set-Cookie has been jarred. Eliminates the fresh-tab
      // race where /api/me + /api/me/preferences hung forever until reload.
      // Fall back to refresh() if the server couldn't assemble the payload
      // (degraded path — first-load 401 race may reappear, but sign-in
      // still works).
      if (res.user !== undefined && res.user !== null) {
        hydrate(res.user);
      } else {
        refresh();
      }
      // Honor ?next= (validated above by safeNextPath). Falls back to /home
      // for plain sign-ins; preserves /oauth/consent?req_id=... for the
      // MCP OAuth bridge round-trip per D-083.
      //
      // For /oauth/* destinations we use window.location.assign rather than
      // navigate() because the consent page is a Pages Function (server-
      // rendered HTML), not a React route — react-router can't load it.
      if (nextPath.startsWith('/oauth/')) {
        window.location.assign(nextPath);
      } else {
        navigate(nextPath);
      }
      // RouteProgressProvider auto-stops the bar on path change; we also
      // explicitly stop so the bar finishes if the next path collides.
      progress.done();
    } catch (err) {
      progress.done();
      if (err instanceof ApiError) {
        // Sub-agent HHHHH (2026-07-29) — auto-recover the default
        // path. Any transient failure (timeout, network, 502/504/5xx)
        // triggers resetAndRedirectToSignIn() rather than parking on
        // a retry callout. Silent recovery beats a 2-button choice.
        // Preserved as manual affordance: after AUTO_RESET_MAX_ATTEMPTS
        // resets in the same session (cap = 2), fall back to the
        // Push 100 retry callout so a persistent outage doesn't loop
        // the reset flow forever.
        const isTransient =
          err.code === 'signin_timeout' ||
          err.code === 'network_failure' ||
          err.status === 502 ||
          err.status === 504 ||
          (err.status >= 500 && err.status < 600);
        const resetCount = getSignInResetCount();
        const canAutoRecover = isTransient && resetCount < AUTO_RESET_MAX_ATTEMPTS;

        if (canAutoRecover) {
          // Fire the auto-recover flow. Hard reload follows in the
          // .finally() of the fire-and-forget /api/signout. Leave the
          // "Signing in…" loading card on screen until the reload
          // takes over — no jarring flip to an error state that's
          // about to vanish.
          resetAndRedirectToSignIn();
          return;
        }

        // Manual-retry branch — 401 (real bad password), reset cap
        // exhausted, or a shape we don't want to auto-recover
        // through (validation-style 4xx). Same envelopes as before
        // Push HHHHH — Push 100's retry / start-fresh affordance
        // stays as the escape hatch when auto-recover has burned
        // its budget.
        if (err.code === 'signin_timeout' || err.status === 504) {
          setError(
            err.status === 504
              ? "The sign-in service didn't respond. Try again — if it keeps happening, check workspace.olytic.com."
              : err.friendly,
          );
          setErrorKind('timeout');
          setTimedOut(true);
        } else if (err.status === 401) {
          setError("Email or password isn't right.");
          setErrorKind('wrong_password');
        } else if (err.status === 502) {
          setError("Couldn't reach the sign-in service. Try again in a moment.");
          setErrorKind('timeout');
          setTimedOut(true);
        } else if (isTransient) {
          // Cap-hit fallback for network_failure / 5xx.
          setError(
            'We hit repeated trouble signing you in. Try again — if it keeps happening, contact support.',
          );
          setErrorKind('timeout');
          setTimedOut(true);
        } else {
          setError(err.message);
          setErrorKind('generic');
        }
      } else {
        setError('Sign in failed. Please try again.');
        setErrorKind('generic');
      }
      setSubmitting(false);
    }
  }

  // While submitting, swap the whole form for a loading card. Avoids the
  // perceived "fields cleared" flicker — what was actually happening was a
  // brief re-render with `disabled` styling on populated inputs before the
  // refresh()/navigate() sequence completed. Loading card removes the
  // ambiguity entirely.
  //
  // Sub-agent NO-ABORT (2026-08-13): loading copy shifts on elapsed-time
  // thresholds via pickAuthProgressCopy so a slow signin (cold Neon,
  // cold Better Auth isolate) reads as "still working" instead of
  // "stuck." 90s SIGNIN_TIMEOUT_MS in signIn() catches genuine hard
  // hangs and routes to the existing auto-recover / manual-retry paths.
  if (submitting) {
    const copy = pickAuthProgressCopy('signin', elapsedMs);
    return (
      <AuthShell title={copy.title} subtitle="One moment.">
        <div className="auth-loading">
          <span className="spinner spinner-lg" />
          <p className="muted" style={{ margin: '14px 0 0', fontSize: '13px' }}>
            {copy.subtitle}
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your Olytic workspace."
      footer={
        <>
          Don't have an account?{' '}
          <Link to="/sign-up" style={{ color: 'var(--olytic-obsidian)', fontWeight: 500 }}>
            Create one
          </Link>
        </>
      }
    >
      {accountClosed && (
        <div className="callout callout-info" style={{ marginBottom: 16 }}>
          <p className="callout-body" style={{ margin: 0 }}>
            Your workspace has been closed. Sign in to a different workspace, or{' '}
            <Link to="/sign-up" style={{ color: 'var(--olytic-teal)' }}>
              create a new one
            </Link>
            .
          </p>
        </div>
      )}
      {/* Sub-agent HHHHH (2026-07-29) — auto-recover flashes.
          `expired` = protected-page session probe failed and the shell
          redirected here. `reset`   = resetAndRedirectToSignIn()
          hard-reloaded after a sign-in fetch failure. Only one shows
          at a time; expired > reset > accountClosed for priority since
          the message a stuck user sees should reflect the most recent
          reason they landed here. Both callouts self-clear on the
          next submit (natural navigation strips ?expired / ?reset). */}
      {!accountClosed && expiredFlash && (
        <div className="callout callout-info" style={{ marginBottom: 16 }}>
          <p className="callout-body" style={{ margin: 0 }}>
            Your session expired. Sign in again to continue.
          </p>
        </div>
      )}
      {!accountClosed && !expiredFlash && resetFlash && (
        <div className="callout callout-info" style={{ marginBottom: 16 }}>
          <p className="callout-body" style={{ margin: 0 }}>
            Sign-in refreshed — try again.
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <div className="form-row">
          <label className="form-label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            required
            disabled={submitting}
            // Round 6 Item 19 — focus the first text input on mount so the
            // page is immediately keyboard-ready. If email was pre-filled
            // from ?email= or workspace-closed redirect, browser autofill
            // handles it; autoFocus still helps clean loads.
            autoFocus={email === ''}
          />
        </div>

        <div className="form-row">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
            }}
          >
            <label className="form-label" htmlFor="password">
              Password
            </label>
            <Link to="/forgot-password" style={{ fontSize: '12px', color: 'var(--olytic-teal)' }}>
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
            disabled={submitting}
          />
        </div>

        {error !== null && (
          <div className="callout callout-danger">
            <p className="callout-body">{error}</p>
            {errorKind === 'wrong_password' && (
              <p className="callout-body" style={{ marginTop: '6px', fontSize: '12px' }}>
                <Link to="/forgot-password" style={{ color: 'var(--olytic-red)' }}>
                  Reset your password
                </Link>{' '}
                or{' '}
                <Link to="/sign-up" style={{ color: 'var(--olytic-red)' }}>
                  create an account
                </Link>
                .
              </p>
            )}
            {errorKind === 'timeout' && (
              <p className="callout-body" style={{ marginTop: '6px', fontSize: '12px' }}>
                Click <strong>Sign in</strong> to try again, or{' '}
                <button
                  type="button"
                  onClick={() => {
                    // Sub-agent HHHHH (2026-07-29) — same auto-recover
                    // primitive used by the automatic path. Manual click
                    // is the escape hatch when auto-recover has burned
                    // its budget (>= AUTO_RESET_MAX_ATTEMPTS resets in
                    // the session). Fire-and-forget /api/signout to
                    // nuke any half-set cookie, then hard-reload to
                    // /sign-in?_r=<ts>&reset=1 for a pristine form.
                    resetAndRedirectToSignIn();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--olytic-red)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                  }}
                >
                  start fresh
                </button>{' '}
                to nuke any stale session state.
              </p>
            )}
          </div>
        )}
        {timedOut && errorKind !== 'timeout' && (
          <div className="callout callout-info" style={{ marginTop: 8, fontSize: 12 }}>
            <p className="callout-body" style={{ margin: 0 }}>
              Previous attempt stalled — this one should go through cleanly.
            </p>
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          {submitting ? (
            <span className="loading-inline">
              <span className="spinner" /> Signing in…
            </span>
          ) : (
            'Sign in'
          )}
        </button>
      </form>
    </AuthShell>
  );
}
